/**
 * Try Chat Temporary Attachments
 *
 * Client-only attachment handling for unauthenticated /try users.
 *
 * Primary responsibilities:
 * - Enforce the free-plan upload ceiling for try chats
 * - Keep temporary file text in memory for the public chat turn
 * - Clear temp attachments when the try chat is cleared
 *
 * Dependencies:
 * - Free-plan attachment limits
 */

import { LOSI_ATTACHMENT_LIMITS } from '@/lib/billing/losi-pricing'

export const TRY_USER_MAX_ATTACHMENT_BYTES =
  LOSI_ATTACHMENT_LIMITS.free.maxAttachmentSizeBytes

export const TRY_USER_MAX_ATTACHMENT_LABEL =
  LOSI_ATTACHMENT_LIMITS.free.maxAttachmentSizeLabel

export const TRY_USER_MAX_FILES_PER_UPLOAD =
  LOSI_ATTACHMENT_LIMITS.free.filesPerUpload ?? 3

export const TRY_TEMP_FILE_ID_PREFIX = 'temp-try-'

/** Keep try prompt payloads under the public tools-stream body budget. */
export const TRY_TEMP_FILE_CONTENT_MAX_CHARS = 40_000

const tryTempFileContents = new Map<string, string>()

export function isTryTempFileId(fileId: string | null | undefined): boolean {
  return Boolean(fileId && fileId.startsWith(TRY_TEMP_FILE_ID_PREFIX))
}

export function createTryTempFileId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${TRY_TEMP_FILE_ID_PREFIX}${crypto.randomUUID()}`
  }

  return `${TRY_TEMP_FILE_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function setTryTempFileContent(fileId: string, content: string) {
  if (!isTryTempFileId(fileId)) return
  const trimmed = content.trim()
  if (!trimmed) return
  tryTempFileContents.set(
    fileId,
    trimmed.length > TRY_TEMP_FILE_CONTENT_MAX_CHARS
      ? `${trimmed.slice(0, TRY_TEMP_FILE_CONTENT_MAX_CHARS)}\n\n[Truncated for try chat]`
      : trimmed
  )
}

export function getTryTempFileContent(fileId: string): string | undefined {
  return tryTempFileContents.get(fileId)
}

export function clearTryTempFileContent(fileId: string) {
  tryTempFileContents.delete(fileId)
}

export function clearAllTryTempFileContents() {
  tryTempFileContents.clear()
}

export function isOverTryAttachmentLimit(sizeBytes: number): boolean {
  return sizeBytes > TRY_USER_MAX_ATTACHMENT_BYTES
}

export function getTryAttachmentOverLimitMessage(fileName?: string): string {
  const subject = fileName ? `"${fileName}" is` : 'That file is'
  return `${subject} larger than ${TRY_USER_MAX_ATTACHMENT_LABEL}. Log in for bigger uploads.`
}

export function getTryAttachmentOverLimitDescription(fileName?: string): string {
  return `${getTryAttachmentOverLimitMessage(fileName)} Free try chats keep attachments temporary until you sign in.`
}

function escapeXmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function buildTryTempFilesPromptBlock(
  files: Array<{ id: string; name: string; type?: string }>
): string {
  const sections = files
    .map(file => {
      const content = getTryTempFileContent(file.id)?.trim()
      if (!content) return null

      return [
        `<try_temp_file name="${escapeXmlAttribute(file.name)}" type="${escapeXmlAttribute(file.type || 'file')}">`,
        content,
        '</try_temp_file>'
      ].join('\n')
    })
    .filter((section): section is string => Boolean(section))

  if (sections.length === 0) return ''

  return [
    '<try_temp_attachments>',
    'Temporary try-chat attachments (session only):',
    ...sections,
    '</try_temp_attachments>'
  ].join('\n\n')
}

const TEXT_LIKE_EXTENSIONS = new Set([
  'txt',
  'md',
  'markdown',
  'csv',
  'tsv',
  'json',
  'js',
  'jsx',
  'ts',
  'tsx',
  'py',
  'go',
  'scala',
  'c',
  'cpp',
  'cs',
  'html',
  'css',
  'php',
  'java',
  'rb',
  'lua',
  'sh',
  'sql',
  'yaml',
  'yml',
  'xml',
  'log'
])

export function isTryTextLikeFile(file: File): boolean {
  if (file.type.startsWith('text/')) return true
  if (
    file.type === 'application/json' ||
    file.type === 'application/xml' ||
    file.type === 'application/javascript'
  ) {
    return true
  }

  const extension = file.name.split('.').pop()?.toLowerCase() || ''
  return TEXT_LIKE_EXTENSIONS.has(extension)
}

export function isTryUnsupportedMediaFile(file: File): boolean {
  if (file.type.startsWith('audio/') || file.type.startsWith('video/')) {
    return true
  }

  const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`
  return [
    '.mp3',
    '.wav',
    '.m4a',
    '.aac',
    '.flac',
    '.ogg',
    '.webm',
    '.aiff',
    '.aif',
    '.mp4',
    '.mov',
    '.mkv',
    '.avi'
  ].includes(extension)
}

export function isTryPdfFile(file: File, simplifiedType?: string): boolean {
  return (
    simplifiedType === 'pdf' ||
    file.type.includes('pdf') ||
    file.name.toLowerCase().endsWith('.pdf')
  )
}

export async function extractTryTempFileText(
  file: File,
  simplifiedType: string
): Promise<string | null> {
  if (simplifiedType === 'docx') {
    const { extractDocxRawText } = await import('@/lib/docx/extract-docx-raw-text')
    const arrayBuffer = await file.arrayBuffer()
    const text = await extractDocxRawText(arrayBuffer)
    return text?.trim() ? text : null
  }

  if (isTryTextLikeFile(file) || simplifiedType === 'txt' || simplifiedType === 'md') {
    const text = await file.text()
    return text.trim() ? text : null
  }

  return null
}
